import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getConversations, getMessages, sendMessage, markRead, deleteConversation } from '../api/conversations';
import { getContacts, createContact } from '../api/contacts';
import { getSessions } from '../api/sessions';
import api from '../api/client';
import { useSocket } from '../context/SocketContext';
import {
  HiOutlinePaperAirplane, HiOutlineMagnifyingGlass, HiOutlinePlusCircle, HiOutlineXMark,
  HiOutlinePhoto, HiOutlineVideoCamera, HiOutlineMusicalNote, HiOutlineMicrophone,
  HiOutlineDocument, HiOutlineMapPin, HiOutlineUser, HiOutlineFaceSmile,
  HiOutlineChatBubbleLeftRight, HiOutlineUserPlus, HiOutlineArrowLeft, HiOutlineCheckCircle,
  HiOutlineTrash, HiOutlineEllipsisVertical, HiOutlineExclamationTriangle
} from 'react-icons/hi2';
import toast from 'react-hot-toast';

// Renders message content based on type
function MessageContent({ msg }) {
  const type = msg.message_type;
  const text = msg.message_text;
  const isOut = msg.direction === 'out';
  const iconClass = `w-4 h-4 inline-block mr-1 ${isOut ? 'text-primary-100' : 'text-dark-400'}`;
  const labelClass = `text-xs italic ${isOut ? 'text-primary-100' : 'text-dark-400'}`;

  switch (type) {
    case 'image':
      return <div><p className={labelClass}><HiOutlinePhoto className={iconClass} /> Photo</p>{text && <p className="mt-1">{text}</p>}</div>;
    case 'video':
      return <div><p className={labelClass}><HiOutlineVideoCamera className={iconClass} /> Video</p>{text && <p className="mt-1">{text}</p>}</div>;
    case 'audio':
      return <p className={labelClass}><HiOutlineMusicalNote className={iconClass} /> Audio</p>;
    case 'ptt':
      return (
        <div className="flex items-center gap-2 min-w-[120px]">
          <HiOutlineMicrophone className={`w-5 h-5 ${isOut ? 'text-primary-100' : 'text-primary-500'}`} />
          <div className={`flex-1 h-1 rounded-full ${isOut ? 'bg-primary-300' : 'bg-dark-200'}`}>
            <div className={`h-1 rounded-full w-3/4 ${isOut ? 'bg-white' : 'bg-primary-500'}`} />
          </div>
        </div>
      );
    case 'file': case 'pdf':
      return <div className="flex items-center gap-2"><HiOutlineDocument className={`w-5 h-5 ${isOut ? 'text-primary-100' : 'text-dark-400'}`} /><span>{text || 'Document'}</span></div>;
    case 'sticker':
      return <p className={labelClass}><HiOutlineFaceSmile className={iconClass} /> Sticker</p>;
    case 'contact':
      return <div className="flex items-center gap-2"><HiOutlineUser className={`w-5 h-5 ${isOut ? 'text-primary-100' : 'text-dark-400'}`} /><span>{text || 'Contact'}</span></div>;
    case 'location':
      return <div className="flex items-center gap-2"><HiOutlineMapPin className={`w-5 h-5 ${isOut ? 'text-primary-100' : 'text-dark-400'}`} /><span>{text || 'Location'}</span></div>;
    case 'poll':
      return <div><p className={labelClass}>Poll</p>{text && <p className="mt-1 font-medium">{text}</p>}</div>;
    case 'system':
      return <p className="italic">{text || 'System message'}</p>;
    default:
      return <p>{text || ''}</p>;
  }
}

// Preview text for conversation list
function PreviewText({ text }) {
  if (!text) return <span>No messages</span>;
  if (text.startsWith('[image]')) return <span className="flex items-center gap-1"><HiOutlinePhoto className="w-3 h-3" /> Photo</span>;
  if (text.startsWith('[video]')) return <span className="flex items-center gap-1"><HiOutlineVideoCamera className="w-3 h-3" /> Video</span>;
  if (text.startsWith('[audio]') || text.startsWith('[ptt]')) return <span className="flex items-center gap-1"><HiOutlineMicrophone className="w-3 h-3" /> Voice message</span>;
  if (text.startsWith('[sticker]')) return <span className="flex items-center gap-1"><HiOutlineFaceSmile className="w-3 h-3" /> Sticker</span>;
  if (text.startsWith('[location]')) return <span className="flex items-center gap-1"><HiOutlineMapPin className="w-3 h-3" /> Location</span>;
  if (text.startsWith('[file]') || text.startsWith('[pdf]')) return <span className="flex items-center gap-1"><HiOutlineDocument className="w-3 h-3" /> Document</span>;
  if (text.startsWith('[contact]')) return <span className="flex items-center gap-1"><HiOutlineUser className="w-3 h-3" /> Contact</span>;
  return <span>{text}</span>;
}

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // New chat state
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatStep, setNewChatStep] = useState('contact'); // 'contact' | 'addContact' | 'compose'
  const [contacts, setContacts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedSession, setSelectedSession] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [startingChat, setStartingChat] = useState(false);
  // Quick add contact
  const [newContactName, setNewContactName] = useState('');
  const [newContactMobile, setNewContactMobile] = useState('');
  const [addingContact, setAddingContact] = useState(false);
  // Chat menu & delete confirm
  const [chatMenu, setChatMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // conversation object

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const menuRef = useRef(null);
  const { subscribe } = useSocket() || {};
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const loadConversations = useCallback(async () => {
    try {
      const res = await getConversations({ search: search || undefined, status: 'open', limit: 50 });
      setConversations(res.data.data.conversations);
    } catch { } finally { setLoading(false); }
  }, [search]);

  // Initial load + slow fallback poll (30s) — socket handles the fast updates
  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(loadConversations, 30000);
    return () => clearInterval(pollRef.current);
  }, [loadConversations]);

  // Socket: real-time conversation & message updates
  useEffect(() => {
    if (!subscribe) return;

    const unsub1 = subscribe('conversation:updated', () => {
      loadConversations();
    });

    const unsub2 = subscribe('message:incoming', (data) => {
      // If we're viewing this conversation, reload messages instantly
      if (selectedRef.current?.id === data.conversation_id) {
        loadMessages(data.conversation_id);
      }
      loadConversations();
    });

    const unsub3 = subscribe('message:sent', (data) => {
      if (selectedRef.current?.id === data.conversation_id) {
        loadMessages(data.conversation_id);
      }
    });

    // Real-time delivery status updates (sent → delivered → read)
    const unsub4 = subscribe('message:status', (data) => {
      if (selectedRef.current?.id === data.conversation_id) {
        // Update message status in-place without full reload for instant tick marks
        setMessages(prev => prev.map(msg => {
          if (msg.id === data.message_id || (data.wa_message_key && msg.wa_message_key === data.wa_message_key)) {
            return { ...msg, status: data.status };
          }
          return msg;
        }));
      }
    });

    return () => { unsub1(); unsub2(); unsub3(); unsub4(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);

  // Load messages when conversation is selected
  useEffect(() => {
    if (selected) {
      loadMessages(selected.id);
    } else {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Close chat menu on outside click
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setChatMenu(false); };
    if (chatMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [chatMenu]);

  const handleDeleteConversation = async (conv) => {
    try {
      await deleteConversation(conv.id);
      toast.success('Conversation deleted');
      setConfirmDelete(null);
      setChatMenu(false);
      if (selected?.id === conv.id) {
        setSelected(null);
        setMessages([]);
      }
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const loadMessages = async (convId) => {
    try {
      const res = await getMessages(convId, { limit: 100 });
      const newMessages = res.data.data.messages;
      // Only update state if messages changed (avoid unnecessary re-renders)
      setMessages((prev) => {
        if (prev.length !== newMessages.length || (newMessages.length > 0 && prev[prev.length - 1]?.id !== newMessages[newMessages.length - 1]?.id)) {
          return newMessages;
        }
        return prev;
      });
      // Mark read if there are unread messages
      const conv = conversations.find((c) => c.id === convId);
      if (conv?.unread_count > 0) {
        await markRead(convId);
        setConversations((prev) => prev.map((c) => c.id === convId ? { ...c, unread_count: 0 } : c));
      }
    } catch { }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      await sendMessage(selected.id, { message_text: text, message_type: 'text' });
      setText('');
      loadMessages(selected.id);
      loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally { setSending(false); }
  };

  // === New Chat Flow ===
  const openNewChat = async () => {
    setShowNewChat(true);
    setNewChatStep('contact');
    setSelectedContact(null);
    setSelectedSession('');
    setFirstMessage('');
    setContactSearch('');
    try {
      const [contactsRes, sessionsRes] = await Promise.all([
        getContacts({ status: 'active', limit: 100 }),
        getSessions()
      ]);
      setContacts(contactsRes.data.data.contacts);
      const allSessions = sessionsRes.data.data.sessions || [];
      // A session is usable if it's connected either live (in-memory) or in DB
      const connected = allSessions.filter(
         (s) => s.live_status === 'connected' || s.status === 'connected'
      );
      // If no live-connected sessions but DB says connected, still allow (user may need to reconnect)
      setSessions(connected.length > 0 ? connected : allSessions.filter(s => s.phone_number));
      if (connected.length === 1) setSelectedSession(connected[0].id.toString());
      else if (connected.length === 0 && allSessions.filter(s => s.phone_number).length === 1) {
        setSelectedSession(allSessions.filter(s => s.phone_number)[0].id.toString());
      }
    } catch { toast.error('Failed to load data'); }
  };

  const closeNewChat = () => {
    setShowNewChat(false);
    setNewChatStep('contact');
    setSelectedContact(null);
    setSelectedSession('');
    setFirstMessage('');
    setContactSearch('');
    setNewContactName('');
    setNewContactMobile('');
  };

  const selectContact = (c) => {
    setSelectedContact(c);
    setNewChatStep('compose');
  };

  const handleQuickAddContact = async (e) => {
    e.preventDefault();
    setAddingContact(true);
    try {
      const res = await createContact({ name: newContactName, mobile: newContactMobile });
      const newContact = res.data.data?.contact || { id: res.data.data?.id, name: newContactName, mobile: newContactMobile };
      toast.success('Contact added');
      setContacts((prev) => [newContact, ...prev]);
      selectContact(newContact);
      setNewContactName('');
      setNewContactMobile('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add contact');
    } finally { setAddingContact(false); }
  };

  const handleStartChat = async (e) => {
    e.preventDefault();
    if (!selectedContact || !selectedSession || !firstMessage.trim()) return;
    setStartingChat(true);
    try {
      await api.post('/messages/send', {
        contact_id: selectedContact.id,
        wa_session_id: parseInt(selectedSession),
        message_type: 'text',
        message_text: firstMessage.trim()
      });
      toast.success('Message sent!');
      closeNewChat();
      await loadConversations();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally { setStartingChat(false); }
  };

  const filteredContacts = contacts.filter((c) =>
    !contactSearch || c.name?.toLowerCase().includes(contactSearch.toLowerCase()) || c.mobile?.includes(contactSearch)
  );

  return (
    <div className="flex h-[calc(100vh-4rem)] lg:h-[calc(100vh-3rem)] bg-white rounded-xl border border-dark-200 overflow-hidden">
      {/* Conversation list */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-dark-200 flex flex-col ${selected ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-3 border-b border-dark-200">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <HiOutlineMagnifyingGlass className="absolute left-3 top-2.5 w-4 h-4 text-dark-400" />
              <input
                type="text" placeholder="Search conversations..." value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-dark-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button onClick={openNewChat} className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors" title="New Chat">
              <HiOutlinePlusCircle className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center p-8">
              <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                <HiOutlineChatBubbleLeftRight className="w-7 h-7 text-primary-400" />
              </div>
              <p className="text-dark-600 text-sm font-medium mb-1">No conversations yet</p>
              <p className="text-dark-400 text-xs mb-4">Start a chat with one of your contacts</p>
              <button onClick={openNewChat} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
                New Chat
              </button>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => setSelected(conv)}
                onContextMenu={(e) => { e.preventDefault(); setConfirmDelete(conv); }}
                className={`group flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-dark-100 hover:bg-gray-50 transition-colors ${
                  selected?.id === conv.id ? 'bg-primary-50' : ''
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                  {conv.contact_name?.[0]?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-sm truncate">{conv.contact_name || conv.contact_mobile}</p>
                    <div className="flex items-center gap-1">
                      {conv.unread_count > 0 && (
                        <span className="bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                          {conv.unread_count}
                        </span>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(conv); }}
                        className="p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete conversation"
                      >
                        <HiOutlineTrash className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-dark-400 truncate flex items-center gap-1">
                    <PreviewText text={conv.last_message_text} />
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className={`flex-1 flex flex-col ${selected ? 'flex' : 'hidden md:flex'}`}>
        {selected ? (
          <>
            <div className="px-4 py-3 border-b border-dark-200 flex items-center gap-3">
              <button className="md:hidden text-dark-500 p-1 hover:bg-gray-100 rounded-lg" onClick={() => setSelected(null)}>
                <HiOutlineArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm">
                {selected.contact_name?.[0]?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{selected.contact_name || selected.contact_mobile}</p>
                <p className="text-xs text-dark-400">{selected.contact_mobile}</p>
              </div>
              {/* Chat actions menu */}
              <div className="relative" ref={menuRef}>
                <button onClick={() => setChatMenu(!chatMenu)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <HiOutlineEllipsisVertical className="w-5 h-5 text-dark-500" />
                </button>
                {chatMenu && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl border border-dark-200 shadow-lg z-20 py-1 animate-fadeIn">
                    <button onClick={() => { setConfirmDelete(selected); setChatMenu(false); }}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors">
                      <HiOutlineTrash className="w-4 h-4" /> Delete Conversation
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
              {messages.length === 0 && (
                <p className="text-center text-dark-400 text-xs py-8">No messages yet</p>
              )}
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] px-3 py-2 rounded-xl text-sm ${
                    msg.direction === 'out'
                      ? 'bg-primary-500 text-white rounded-br-sm'
                      : 'bg-white border border-dark-200 rounded-bl-sm'
                  }`}>
                    <MessageContent msg={msg} />
                    <div className={`flex items-center gap-1 mt-1 ${msg.direction === 'out' ? 'text-primary-100' : 'text-dark-300'}`}>
                      <span className="text-[10px]">
                        {new Date(msg.message_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {msg.direction === 'out' && (
                        <span className={`text-[10px] ${msg.status === 'read' ? 'text-blue-300' : ''}`}>
                          {msg.status === 'read' ? '✓✓'
                            : msg.status === 'delivered' ? '✓✓'
                            : msg.status === 'sent' ? '✓'
                            : msg.status === 'failed' ? '✕'
                            : '⏳'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSend} className="p-3 border-t border-dark-200 flex gap-2">
              <input
                type="text" value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-dark-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button type="submit" disabled={sending || !text.trim()}
                className="px-4 py-2.5 bg-primary-500 text-white rounded-xl hover:bg-primary-600 disabled:opacity-50 transition-colors">
                <HiOutlinePaperAirplane className="w-5 h-5" />
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary-50 flex items-center justify-center">
                <HiOutlineChatBubbleLeftRight className="w-10 h-10 text-primary-300" />
              </div>
              <p className="text-sm font-semibold text-dark-700 mb-1">WhatsApp Messaging</p>
              <p className="text-xs text-dark-400 mb-5">Select a conversation or start a new chat</p>
              <button onClick={openNewChat} className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all shadow-sm">
                <HiOutlinePlusCircle className="w-4 h-4" /> New Chat
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ====== New Chat Modal ====== */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && closeNewChat()}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl animate-fadeIn max-h-[85vh] flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-dark-200 flex-shrink-0">
              {newChatStep !== 'contact' && (
                <button onClick={() => setNewChatStep('contact')} className="p-1 hover:bg-gray-100 rounded-lg">
                  <HiOutlineArrowLeft className="w-5 h-5 text-dark-500" />
                </button>
              )}
              <h2 className="font-bold text-dark-800 flex-1">
                {newChatStep === 'contact' ? 'New Chat' : newChatStep === 'addContact' ? 'Add Contact' : 'Send Message'}
              </h2>
              <button onClick={closeNewChat} className="p-1 hover:bg-gray-100 rounded-lg">
                <HiOutlineXMark className="w-5 h-5 text-dark-400" />
              </button>
            </div>

            {/* No connected session warning */}
            {sessions.length === 0 && newChatStep === 'contact' && (
              <div className="p-6 text-center flex-1">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-yellow-50 flex items-center justify-center">
                  <svg className="w-7 h-7 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                </div>
                <p className="font-semibold text-dark-700 mb-1">No WhatsApp connected</p>
                <p className="text-sm text-dark-400 mb-5">Connect a WhatsApp session first to start messaging.</p>
                <a href="/sessions" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600">
                  Go to Sessions
                </a>
              </div>
            )}

            {/* Step 1: Pick a contact */}
            {sessions.length > 0 && newChatStep === 'contact' && (
              <div className="flex flex-col flex-1 overflow-hidden">
                {/* Search bar */}
                <div className="p-3 border-b border-dark-100">
                  <div className="relative">
                    <HiOutlineMagnifyingGlass className="absolute left-3 top-2.5 w-4 h-4 text-dark-400" />
                    <input
                      type="text" autoFocus placeholder="Search by name or phone number..."
                      value={contactSearch} onChange={(e) => setContactSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-dark-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>

                {/* Quick add contact button */}
                <button onClick={() => { setNewChatStep('addContact'); setNewContactName(''); setNewContactMobile(contactSearch || ''); }}
                  className="flex items-center gap-3 px-4 py-3 border-b border-dark-100 hover:bg-primary-50 transition-colors text-left">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <HiOutlineUserPlus className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-primary-600">Add new contact</p>
                    <p className="text-xs text-dark-400">Create a contact and start chatting</p>
                  </div>
                </button>

                {/* Contact list */}
                <div className="flex-1 overflow-y-auto">
                  {filteredContacts.length === 0 ? (
                    <p className="p-6 text-sm text-dark-400 text-center">No contacts found</p>
                  ) : (
                    filteredContacts.map((c) => (
                      <div key={c.id} onClick={() => selectContact(c)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-dark-100 transition-colors">
                        <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                          {c.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-dark-800 truncate">{c.name}</p>
                          <p className="text-xs text-dark-400">{c.mobile}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Step 1b: Quick add contact */}
            {newChatStep === 'addContact' && (
              <form onSubmit={handleQuickAddContact} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1.5">Name</label>
                  <input type="text" required autoFocus placeholder="Contact name"
                    value={newContactName} onChange={(e) => setNewContactName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-dark-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-dark-700 mb-1.5">WhatsApp Number</label>
                  <input type="text" required placeholder="e.g. 923001234567 (with country code)"
                    value={newContactMobile} onChange={(e) => setNewContactMobile(e.target.value)}
                    className="w-full px-4 py-2.5 border border-dark-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-dark-400 mt-1.5">Enter the full number with country code, no + or spaces</p>
                </div>
                <button type="submit" disabled={addingContact || !newContactName.trim() || !newContactMobile.trim()}
                  className="w-full py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                  {addingContact ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Adding...</>
                  ) : (
                    <><HiOutlineCheckCircle className="w-4 h-4" /> Add & Continue</>
                  )}
                </button>
              </form>
            )}

            {/* Step 2: Compose message */}
            {newChatStep === 'compose' && selectedContact && (
              <form onSubmit={handleStartChat} className="flex flex-col flex-1 overflow-hidden">
                <div className="p-4 space-y-4 overflow-y-auto flex-1">
                  {/* Selected contact card */}
                  <div className="flex items-center gap-3 p-3 bg-primary-50 rounded-xl border border-primary-100">
                    <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm">
                      {selectedContact.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-dark-800">{selectedContact.name}</p>
                      <p className="text-xs text-dark-400">{selectedContact.mobile}</p>
                    </div>
                    <button type="button" onClick={() => { setSelectedContact(null); setNewChatStep('contact'); }}
                      className="text-dark-400 hover:text-dark-600 p-1"><HiOutlineXMark className="w-4 h-4" /></button>
                  </div>

                  {/* Session picker (only if multiple sessions) */}
                  {sessions.length > 1 && (
                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1.5">Send from</label>
                      <select value={selectedSession} onChange={(e) => setSelectedSession(e.target.value)} required
                        className="w-full px-4 py-2.5 border border-dark-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500 bg-white">
                        <option value="">Select WhatsApp session</option>
                        {sessions.map((s) => (
                          <option key={s.id} value={s.id}>{s.session_name} {s.phone_number ? `(${s.phone_number})` : ''}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {sessions.length === 1 && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-xl border border-green-100 text-xs text-green-700">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      Sending via <strong>{sessions[0].session_name}</strong> {sessions[0].phone_number ? `(${sessions[0].phone_number})` : ''}
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1.5">Message</label>
                    <textarea required rows={4} autoFocus placeholder="Type your message..."
                      value={firstMessage} onChange={(e) => setFirstMessage(e.target.value)}
                      className="w-full px-4 py-3 border border-dark-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                  </div>
                </div>

                <div className="p-4 border-t border-dark-200 flex-shrink-0">
                  <button type="submit"
                    disabled={!selectedSession || !firstMessage.trim() || startingChat}
                    className="w-full py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                    {startingChat ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>
                    ) : (
                      <><HiOutlinePaperAirplane className="w-4 h-4" /> Send Message</>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ====== Delete Confirmation Dialog ====== */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl animate-fadeIn p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <HiOutlineExclamationTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="font-bold text-dark-800">Delete Conversation</h3>
            </div>
            <p className="text-sm text-dark-500 mb-2">
              Delete the conversation with <strong>{confirmDelete.contact_name || confirmDelete.contact_mobile}</strong>?
            </p>
            <p className="text-xs text-dark-400 mb-6">
              All messages in this conversation will be permanently deleted from the database. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-dark-200 rounded-xl text-sm font-medium text-dark-600 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={() => handleDeleteConversation(confirmDelete)}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2">
                <HiOutlineTrash className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
